import moment from "moment/moment";

const MAX_SECONDS = 315_576_000; // 10 years

export function msToTime(ms: number): string {
  if (ms === 0) {
    return "None";
  }
  const seconds = (ms / 1000).toFixed(0);
  const minutes = (ms / (1000 * 60)).toFixed(1);
  const hours = (ms / (1000 * 60 * 60)).toFixed(1);
  if (Number(seconds) < 60) return seconds + " Seconds";
  else if (Number(minutes) < 60) return minutes + " Minutes";
  else if (Number(hours) < 100) return hours + " Hours";
  else return Math.trunc(Number(hours)).toLocaleString() + " Hours";
}

export function msToHours(ms: number): string {
  const n = ms / (1000 * 60 * 60 * 24);
  if (n > 100) {
    return `${Math.trunc(n).toLocaleString()} Hours`;
  } else {
    return `${n.toFixed(2)} Hours`;
  }
}

export function formatSecs(secs: number | undefined): string {
  if (secs === undefined) {
    return "None";
  }
  return msToTime(secs * 1000);
}

export function formatSecsToHHMMSS(secs: number): string {
  const seconds = secs % 60;
  const minutes = Math.trunc((secs % 3600) / 60);
  const hours = Math.trunc(secs / 3600);
  const fmt = (n: number) => {
    if (String(n).length >= 2) {
      return String(n);
    }
    return "0" + String(n);
  };
  return `${fmt(hours)}:${fmt(minutes)}:${fmt(seconds)}`;
}

export const fmtGraphDate = (d: string): string => {
  const date = new Date(d);
  return moment.utc(date.getTime()).format("M/DD ha");
};

export function formatSecsShort(secs: number): string {
  const hours = Math.floor(secs / 3600);
  const minutes = Math.floor((secs % 3600) / 60);
  const seconds = secs % 60;
  let out = `${seconds}s`;
  if (minutes > 0) {
    out = `${minutes}m` + out;
  }
  if (hours > 0) {
    out = `${hours}h` + out;
  }
  return out;
}

// parseSecsShort _will_ throw an error if the string is not formatted exactly according to
// ([\d]+h)?([\d]+m)?([\d]+s)?, where at least one of hours, minutes, seconds is required
export function parseSecsShort(secsS: string): number {
  const regex = /^(?:([\d]+)h)?(?:([\d]+)m)?(?:([\d]+)s)?$/;
  const matches = [...(secsS.match(regex) ?? [])];
  if (matches.length !== 4) {
    throw new Error("Incorrectly formatted seconds.");
  } else if (
    matches[1] === undefined &&
    matches[2] === undefined &&
    matches[3] === undefined
  ) {
    throw new Error("Must have at least one of seconds, minutes, hours.");
  }
  let tot = 0;
  if (matches[1] !== undefined) {
    const hours = Number(matches[1]);
    if (isNaN(hours)) {
      throw new Error("Incorrectly formatted hours.");
    }
    tot += hours * 3600;
  }
  if (matches[2] !== undefined) {
    const minutes = Number(matches[2]);
    if (isNaN(minutes)) {
      throw new Error("Incorrectly formatted minutes.");
    }
    tot += minutes * 60;
  }
  if (matches[3] !== undefined) {
    const seconds = Number(matches[3]);
    if (isNaN(seconds)) {
      throw new Error("Incorrectly formatted seconds.");
    }
    tot += seconds;
  }

  return tot;
}

export function formatSecsShortRange(start: number, end: number) {
  return `${formatSecsShort(start)}-${formatSecsShort(end)}`;
}

// Parse a time range expressed as dash-delimited numbers suffixed with "s", "m", "h", or "hrs".
// Allow for a shared suffix that only appears at the end of the second number, and support
// open ranges with a number and unit suffix plus "+". Samples: "1-30s", "30s-1m", "1-2h", "4hrs+".
export function parseSecsShortRange(s: string): [number, number] {
  const parts = s.split("-");
  if (parts.length !== 2) {
    // if "<N>(s|m|h|hrs)+", return the first part as the start and MAX_SECONDS as end
    if (parts.length === 1 && parts[0].match(/^\d+(s|m|h|hrs)\+$/)) {
      parts[0] = fixHours(parts[0]);
      parts[0] = parts[0].replace(/\+$/, "");
      return [parseSecsShort(parts[0]), MAX_SECONDS];
    }
    throw new Error("Incorrectly formatted range");
  }

  for (let i = 0; i < 2; i++) {
    parts[i] = fixHours(parts[i]);
  }

  // support prefix shared by both ("1-5s", "5-15m", "1-2hrs", etc)
  if (parts[0].match(/^\d+$/)) {
    const unit = parts[1].match(/[a-zA-Z]+/);
    if (unit === null) {
      throw new Error("No unit found in second part of range");
    }
    parts[0] = parts[0] + unit[0];
  }

  const start = parseSecsShort(parts[0]);
  const end = parseSecsShort(parts[1]);
  return [start, end];
}

export function yesterday(): Date {
  const today = new Date();
  const dayDuration = 24 * 60 * 60 * 1000;
  return new Date(today.getTime() - dayDuration);
}

export function nHoursAgo(n: number): Date {
  const today = new Date();
  const hourDuration = 60 * 60 * 1000;
  return new Date(today.getTime() - hourDuration * n);
}

export function nMinutesAgo(n: number): Date {
  const today = new Date();
  const minuteDuration = 60 * 1000;
  return new Date(today.getTime() - minuteDuration * n);
}

function fixHours(s: string): string {
  return s.replace(/(\d+)(hrs)/, "$1h");
}
